import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import RichText from "@/components/RichText";
import { useAuth } from "@/context/AuthContext";
import { PageHero } from "@/components/PageHero";
import Seo from "@/components/Seo";
import { HERO_ART } from "@/lib/heroes";
import { fireGamify } from "@/lib/gamify";
import { fetchGamify } from "@/lib/api";
import { CheckCircle2, Flame, Circle } from "lucide-react";

// Sandhyavandana — the traditional trikala (thrice-daily) upasana following the
// standard Smarta vidhi. Mantras given are the canonical, widely-attested forms.
const STEPS = [
  { deva: "आचमन", en: "Achamana",
    mantra: "ॐ केशवाय नमः · ॐ नारायणाय नमः · ॐ माधवाय नमः",
    translit: "Om Keshavaya / Narayanaya / Madhavaya Namah",
    note_en: "Sip water thrice with the three names, then touch the sense organs with the remaining names (Govinda, Vishnu … Krishna).",
    note_hi: "तीन नामों के साथ तीन बार आचमन करें, फिर शेष नामों (गोविन्द, विष्णु … कृष्ण) से इन्द्रियों का स्पर्श करें।" },
  { deva: "प्राणायाम", en: "Pranayama",
    mantra: "ॐ भूः ॐ भुवः ॐ स्वः ॐ महः ॐ जनः ॐ तपः ॐ सत्यम् · ॐ तत्सवितुर्वरेण्यं भर्गो देवस्य धीमहि धियो यो नः प्रचोदयात् · ॐ आपो ज्योती रसोऽमृतं ब्रह्म भूर्भुवः स्वरोम्",
    translit: "Om bhuh … satyam · Om tat savitur … prachodayat · Om apo jyoti raso'mritam brahma bhur bhuvah svar-om",
    note_en: "Regulate the breath (puraka–kumbhaka–rechaka) while mentally reciting the seven vyahritis, the Gayatri and the Gayatri-shiras.",
    note_hi: "सप्त व्याहृति, गायत्री एवं गायत्री-शिरस् का मानसिक जप करते हुए प्राणायाम (पूरक–कुम्भक–रेचक) करें।" },
  { deva: "संकल्प", en: "Sankalpa",
    mantra: "ॐ … प्रातः/माध्याह्निक/सायं सन्ध्याम् उपासिष्ये।",
    translit: "Om … pratah / madhyahnika / sayam sandhyam upasishye.",
    note_en: "Resolve to perform the morning, midday or evening sandhya, recalling the day and place.",
    note_hi: "तिथि एवं स्थान का स्मरण करते हुए प्रातः, माध्याह्निक अथवा सायं सन्ध्या का संकल्प लें।" },
  { deva: "मार्जनम्", en: "Marjana (sprinkling)",
    mantra: "ॐ आपो हि ष्ठा मयोभुवः · ता न ऊर्जे दधातन · महे रणाय चक्षसे",
    translit: "Om apo hi shtha mayobhuvah · ta na urje dadhatana · mahe ranaya chakshase",
    note_en: "Sprinkle water over the head and body with darbha or the fingers, invoking the purifying waters.",
    note_hi: "जल की पवित्रता का आवाहन करते हुए दर्भ अथवा अंगुलियों से सिर एवं शरीर पर जल का प्रोक्षण करें।" },
  { deva: "अर्घ्य प्रदान", en: "Arghya to Surya",
    mantra: "ॐ भूर्भुवः स्वः तत्सवितुर्वरेण्यं भर्गो देवस्य धीमहि धियो यो नः प्रचोदयात्",
    translit: "Om bhur bhuvah svah · tat savitur varenyam bhargo devasya dhimahi · dhiyo yo nah prachodayat",
    note_en: "Offer arghya (water) to Surya thrice while reciting the Gayatri, standing facing east (morning) or west (evening).",
    note_hi: "पूर्व (प्रातः) अथवा पश्चिम (सायं) की ओर मुख कर गायत्री का पाठ करते हुए सूर्य को तीन बार अर्घ्य दें।" },
  { deva: "गायत्री जप", en: "Gayatri Japa",
    mantra: "ॐ भूर्भुवः स्वः तत्सवितुर्वरेण्यं भर्गो देवस्य धीमहि धियो यो नः प्रचोदयात् ॥",
    translit: "Om bhur bhuvah svah · tat savitur varenyam · bhargo devasya dhimahi · dhiyo yo nah prachodayat",
    note_en: "Japa of the Gayatri Mantra (Rigveda 3.62.10) — 10, 28 or 108 times upon a mala, meditating on Savitr.",
    note_hi: "गायत्री मंत्र (ऋग्वेद ३.६२.१०) का जप — सवितृ का ध्यान करते हुए माला पर १०, २८ अथवा १०८ बार।" },
  { deva: "उपस्थान एवं अभिवादन", en: "Upasthana & Abhivadana",
    mantra: "ॐ मित्रस्य चर्षणीधृतः … · (कुल-प्रवर) अभिवादये",
    translit: "Om mitrasya charshani-dhritah … · abhivadaye",
    note_en: "Offer the concluding upasthana to Surya, salute the sandhya devatas and the guru-parampara, and conclude with achamana.",
    note_hi: "सूर्य का उपस्थान करें, सन्ध्या-देवताओं एवं गुरु-परंपरा को अभिवादन करें, फिर आचमन से समापन करें।" },
];

export default function SandhyaVandana() {
  const { pick } = useLang();
  const { page } = useSettings();
  const P = page("/sandhya-vandana");
  const pv = (o, d) => (pick(o?.hi, o?.en) || d);
  const { user } = useAuth();
  const [g, setG] = useState(null);
  const [done, setDone] = useState(false);
  const today = new Date().toDateString();

  useEffect(() => {
    if (user) fetchGamify().then(setG).catch(() => {});
    if (localStorage.getItem("dharmartha_sandhya_day") === today) setDone(true);
  }, [user, today]);

  const complete = async () => {
    const res = await fireGamify("sandhya");
    localStorage.setItem("dharmartha_sandhya_day", today);
    setDone(true);
    if (res?.profile) setG(res.profile);
  };

  return (
    <div data-testid="sandhya-vandana-page">
      <Seo path="/sandhya-vandana" title="Sandhya Vandana — Trikala Upasana with Mantras"
        description="The traditional thrice-daily Sandhyavandana — achamana, pranayama, marjana, arghya to Surya, Gayatri japa and upasthana — with canonical Sanskrit mantras, transliteration and meaning." />
      <PageHero art={HERO_ART.cosmos} overline={pv(P.subtitle, pick("साधना · संध्या वंदना", "Practice · Sandhya Vandana"))}
        deva="संध्या वंदना" en={pv(P.title, pick("संध्या वंदना — त्रिकाल उपासना", "Sandhya Vandana — Trikala Upasana"))}
        tagline={pv(P.explainer, pick("प्रातः, मध्याह्न एवं सायं की नित्य संध्या-उपासना की प्रामाणिक विधि — आचमन, प्राणायाम, अर्घ्य एवं गायत्री जप सहित।", "The traditional morning, midday and evening sandhya — achamana, pranayama, arghya to Surya and Gayatri japa, with canonical mantras."))} />
      {(P.body?.en || P.body?.hi) && (
        <section className="mx-auto max-w-3xl px-5 pt-10 md:px-8" data-testid="sandhya-body">
          <RichText value={pick(P.body?.hi, P.body?.en)} className="max-w-none font-serif-latin text-lg leading-relaxed text-ink-2" />
        </section>
      )}

      {/* Streak / completion */}
      <section className="mx-auto max-w-3xl px-5 pt-10 md:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 border border-line bg-surface p-5">
          <div className="flex items-center gap-6">
            <div><p className="font-serif-latin text-3xl text-saffron" data-testid="sandhya-streak-count">{g?.sandhya_streak ?? 0}</p><p className="text-xs text-ink-3">{pick("दिन निरंतर", "day streak")}</p></div>
            <div><p className="font-serif-latin text-3xl text-ink" data-testid="sandhya-days-count">{g?.sandhya_days ?? 0}</p><p className="text-xs text-ink-3">{pick("कुल दिन", "total days")}</p></div>
          </div>
          {user ? (
            <button onClick={complete} disabled={done} data-testid="sandhya-complete-button"
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-colors ${done ? "border border-green-600 text-green-600" : "bg-[var(--saffron)] text-white hover:bg-[var(--saffron-dark)]"}`}>
              {done ? <><CheckCircle2 className="h-4 w-4" /> {pick("आज पूर्ण", "Completed today")}</> : <><Flame className="h-4 w-4" /> {pick("आज की संध्या वंदना पूर्ण करें", "Mark today's Sandhya complete")}</>}
            </button>
          ) : (
            <Link to="/login" className="text-sm text-saffron" data-testid="sandhya-login-link">{pick("संकल्प संजोने हेतु लॉगिन करें", "Sign in to track your streak")}</Link>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 pt-10 md:px-8">
        <div className="grid grid-cols-3 gap-3 text-center">
          {[["प्रातः", "Pratah", "at sunrise"], ["माध्याह्निक", "Madhyahnika", "around noon"], ["सायं", "Sayam", "at sunset"]].map(([d, e, w]) => (
            <div key={e} className="border border-line bg-surface p-4">
              <p className="font-deva text-lg text-saffron">{d}</p>
              <p className="text-sm text-ink">{e}</p>
              <p className="mt-1 text-xs text-ink-3">{w}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-12 md:px-8">
        <ol className="space-y-4">
          {STEPS.map((s, i) => (
            <li key={i} data-testid={`sandhya-step-${i}`} className="border border-line bg-surface p-5">
              <div className="flex items-baseline gap-3">
                <Circle className="mt-1.5 h-2 w-2 shrink-0 fill-gold text-gold" />
                <div className="min-w-0">
                  <p className="font-deva text-xl text-ink deva-lh">{s.deva} <span className="font-serif-latin text-base text-ink-3">· {s.en}</span></p>
                  <p className="mt-2 font-deva text-lg leading-relaxed text-saffron deva-lh">{s.mantra}</p>
                  <p className="mt-1 text-sm italic text-ink-3">{s.translit}</p>
                  <p className="mt-2 text-sm leading-relaxed text-ink-2">{pick(s.note_hi, s.note_en)}</p>
                </div>
              </div>
            </li>
          ))}
        </ol>
        <p className="mt-8 border-l-2 border-gold/50 pl-4 text-sm leading-relaxed text-ink-3">
          {pick("यह सामान्य स्मार्त सन्ध्यावन्दन विधि पर आधारित है। शाखा, वेद एवं कुल परंपरा अनुसार क्रम एवं मंत्रों में भिन्नता होती है — कृपया अपने गुरु/आचार्य के निर्देशानुसार अभ्यास करें।",
            "This follows the standard Smarta Sandhyavandana. The sequence and mantras vary by shakha, Veda and family tradition — please practise as guided by your guru/acharya.")}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/mantras/gayatri-mantra" className="border border-line px-4 py-2 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink">{pick("गायत्री मंत्र", "Gayatri Mantra")}</Link>
          <Link to="/dainik-pooja" className="border border-line px-4 py-2 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink">{pick("दैनिक पूजा", "Daily Pooja")}</Link>
        </div>
      </section>
    </div>
  );
}
